import { always, complement, cond, equals } from 'ramda';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import TextLink, { ITextLinkProps } from '@ui/atoms/TextLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import { medicationDeliveryConstants } from '@ui/routes/shared/MedicationDeliveryStep';

import aligned from '@ui/styled/Aligned';
import spaced, { ISpaced } from '@ui/styled/Spaced';

import { IPageProps } from '@folx-types/flow-steps';

export interface IMedicationDeliveryPostSubmitStepPageContent {
	heading: string;
	capsulePharmacyText: string;
	folxPharmacyText: string;
	capsuleInfoLinkText: string;
	capsuleInfoLinkUrl: string;
	returnButtonText: string;
}

interface IMedicationDeliveryPostSubmitStepProps extends IPageProps {
	pageContent: IMedicationDeliveryPostSubmitStepPageContent;
	deliveryMethod?: string;
}

const AlignedButton = aligned<IButtonProps & ISpaced>(spaced<IButtonProps>(Button));
const SpacedHeadingText = spaced(HeadingText);

const SpacedTextLink = styled(spaced<ITextLinkProps>(TextLink))`
	width: max-content;
`;

const MedicationDeliveryPostSubmitStep: FC<IMedicationDeliveryPostSubmitStepProps> = ({
	deliveryMethod,
	pageContent,
}) => {
	const [pharmacyText, setPharmacyText] = useState('');

	const {
		heading,
		capsulePharmacyText,
		folxPharmacyText,
		capsuleInfoLinkText,
		capsuleInfoLinkUrl,
		returnButtonText,
	} = pageContent;

	useEffect(() => {
		const pharmacyText = cond([
			[equals(medicationDeliveryConstants.FOLX_METHOD), always(folxPharmacyText)],
			[equals(medicationDeliveryConstants.CAPSULE_METHOD), always(capsulePharmacyText)],
		])(deliveryMethod ?? '');

		const notEquals = complement((arg0: unknown, arg1: unknown) => equals(arg0, arg1));

		setPharmacyText(pharmacyText);
	}, [deliveryMethod]);

	return (
		<FlexColumn>
			<SpacedHeadingText marginBottom="md">{heading}</SpacedHeadingText>
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pharmacyText }} />
			{deliveryMethod === medicationDeliveryConstants.CAPSULE_METHOD && (
				<SpacedTextLink
					marginTop="md"
					href={capsuleInfoLinkUrl}
					label={capsuleInfoLinkText}
					variant="bold"
					newTab
				/>
			)}
			<AlignedButton
				alignSelf="center"
				marginTop="lgxl"
				label={returnButtonText}
				labelSize="sm"
				href="../.."
			/>
		</FlexColumn>
	);
};

export default MedicationDeliveryPostSubmitStep;
